import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Filter, X } from 'lucide-react';
import { getDocuments, type DocumentGroup, type DocumentOption } from '../api/documents';
import { useAskFilters } from './AskFiltersContext';
import { useT } from '../I18nContext';

/**
 * Document multi-select on the rail.
 *
 * Closed: button shows "All Documents" / "3 documents" / "Topic name"
 * depending on selection.
 * Open: popover with optional groups list above a filterable
 * document list. Click outside / Esc closes.
 *
 * Group selection expands to its constituent doc ids - the server
 * doesn't know about groups, just doc ids.
 *
 * Loading state: button is disabled until /api/documents resolves
 * so we don't try to filter against an unknown set.
 */
export function DocumentFilter() {
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const { documentIds, setDocumentIds, clearAll } = useAskFilters();
  const t = useT();

  const docsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: getDocuments,
    // Document list rarely changes within a session; admins
    // re-upload occasionally. Five-min stale time keeps the
    // dropdown snappy without going dangerously stale.
    staleTime: 5 * 60_000,
    retry: 1,
  });

  // Click-outside + Esc to close.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const allDocs = docsQuery.data?.documents ?? [];
  const groups = docsQuery.data?.groups ?? [];
  const groupLabel = docsQuery.data?.group_label ?? t('document_filter.default_group_label');

  // Documents minus the "All Documents" sentinel - that one is
  // represented by an empty selection, not by being checked.
  const selectableDocs = useMemo(
    () => allDocs.filter((d) => d.id !== 'all'),
    [allDocs],
  );

  const visibleDocs = useMemo(() => {
    if (!filterText.trim()) return selectableDocs;
    const needle = filterText.trim().toLowerCase();
    return selectableDocs.filter((d) =>
      (d.title || '').toLowerCase().includes(needle),
    );
  }, [selectableDocs, filterText]);

  // Button label captures the active selection in one short string.
  const label = useMemo(() => {
    if (documentIds.size === 0) return t('document_filter.all_documents');
    if (documentIds.size === 1) {
      const only = [...documentIds][0];
      const doc = selectableDocs.find((d) => d.id === only);
      return doc?.title ?? t('document_filter.one_document_fallback');
    }
    return t('document_filter.n_documents', { count: String(documentIds.size) });
  }, [documentIds, selectableDocs, t]);

  function toggleDoc(id: string) {
    const next = new Set(documentIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setDocumentIds(next);
  }

  function selectGroup(group: DocumentGroup) {
    // Replace selection with the group's doc ids - picking a
    // group is a "scope to this topic" gesture, not an "add to
    // existing selection" one. Matches the legacy behaviour.
    setDocumentIds(new Set(group.document_ids));
    setOpen(false);
  }

  function selectAll() {
    clearAll();
    setOpen(false);
  }

  return (
    <div className="rail-filter" ref={wrapRef}>
      <button
        type="button"
        className={
          'rail-filter-trigger' + (documentIds.size > 0 ? ' is-active' : '')
        }
        onClick={() => setOpen((v) => !v)}
        disabled={docsQuery.isLoading}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('document_filter.trigger_title')}
      >
        <Filter size={13} />
        <span className="rail-filter-trigger-text">{label}</span>
        <ChevronDown size={12} />
      </button>

      {open && (
        <div className="rail-filter-panel">
          <div className="rail-filter-search">
            <input
              type="text"
              placeholder={t('document_filter.filter_placeholder')}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              autoFocus
              aria-label={t('document_filter.filter_aria_label')}
            />
          </div>

          {/* "All Documents" reset row - clearer than a separate
              clear button at the bottom. */}
          <button
            type="button"
            className={
              'rail-filter-row rail-filter-row-all' +
              (documentIds.size === 0 ? ' is-selected' : '')
            }
            onClick={selectAll}
          >
            <span>{t('document_filter.all_documents')}</span>
          </button>

          {groups.length > 0 && (
            <div className="rail-filter-section">
              <div className="rail-filter-section-label">{groupLabel}</div>
              {groups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="rail-filter-row rail-filter-row-group"
                  onClick={() => selectGroup(g)}
                >
                  <span>{g.name}</span>
                  <span className="rail-filter-row-count">
                    {g.document_ids.length}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="rail-filter-section">
            <div className="rail-filter-section-label">{t('document_filter.documents_section_label')}</div>
            {visibleDocs.length === 0 && (
              <div className="rail-filter-empty">{t('document_filter.no_matches')}</div>
            )}
            {visibleDocs.map((d: DocumentOption) => {
              const checked = documentIds.has(d.id);
              return (
                <label
                  key={d.id}
                  className={
                    'rail-filter-row rail-filter-row-doc' +
                    (checked ? ' is-selected' : '')
                  }
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleDoc(d.id)}
                  />
                  <span>{d.title}</span>
                </label>
              );
            })}
          </div>

          {documentIds.size > 0 && (
            <div className="rail-filter-footer">
              <button
                type="button"
                className="rail-filter-clear"
                onClick={() => clearAll()}
              >
                <X size={12} />
                {t('document_filter.clear_selection')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
